/**
 * The photos attached to one thing — a place, a person.
 *
 * A strip rather than a gallery: on a place page the map is the subject and
 * the photos are supporting detail, so they sit in a row you can scan and add
 * to without leaving the page. Same component for both entities, because
 * "pictures of a thing in my life" is one idea.
 *
 * Uploads go straight up on pick — there is no draft state to abandon, so an
 * extra confirm would be a second click for a decision already made. Removal
 * DOES confirm, because bytes do not come back.
 *
 * Images only (owner, 2026-09-08). The video branches below are kept because
 * the server still reports `is_video` honestly and an older row could carry
 * one; they cost nothing and mean a re-enable needs no work here.
 */
import { useEffect, useRef, useState } from 'react'
import { ImagePlus, Loader2, Play, Trash2 } from 'lucide-react'
import {
  MEDIA_ACCEPT, mediaService, sizeWord,
  type MediaItem, type MediaOwnerKind,
} from '../../services/mediaService'

interface Props {
  kind: MediaOwnerKind
  ownerId: number
  /** What the person is adding pictures OF, for the empty line and alt text. */
  label: string
  className?: string
}

export function MediaStrip({ kind, ownerId, label, className = '' }: Props) {
  const [items, setItems] = useState<MediaItem[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<MediaItem | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let cancelled = false
    mediaService
      .listFor(kind, ownerId)
      .then((rows) => { if (!cancelled) setItems(rows) })
      .catch(() => undefined)   // an empty strip is a fine failure mode
    return () => { cancelled = true }
  }, [kind, ownerId])

  async function pick(files: FileList | null) {
    if (!files || files.length === 0) return
    setBusy(true)
    setError(null)
    // One at a time and in order, so a rejected file names itself rather than
    // failing anonymously inside a batch.
    for (const file of Array.from(files)) {
      try {
        const row = await mediaService.upload(kind, ownerId, file)
        setItems((prev) => [...prev, row])
      } catch (e) {
        setError(`${file.name}: ${e instanceof Error ? e.message : 'could not be saved'}`)
        break
      }
    }
    setBusy(false)
    if (fileRef.current) fileRef.current.value = ''
  }

  async function remove(item: MediaItem) {
    if (!window.confirm(`Remove this ${item.is_video ? 'video' : 'photo'}? It cannot be undone.`)) return
    try {
      await mediaService.remove(item.media_id)
      setItems((prev) => prev.filter((i) => i.media_id !== item.media_id))
      if (open?.media_id === item.media_id) setOpen(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove that')
    }
  }

  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-app-micro font-medium text-secondary">
          Photos
        </span>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid="media-add"
          className="inline-flex items-center gap-1.5 rounded-lg border border-soft px-2.5 py-1 text-app-micro text-secondary hover:bg-surface-alt disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
          {busy ? 'Adding…' : 'Add'}
        </button>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept={MEDIA_ACCEPT}
        multiple
        hidden
        onChange={(e) => void pick(e.target.files)}
        data-testid="media-input"
      />

      {items.length === 0 ? (
        <p className="mt-2 text-app-micro text-muted">
          {/* A place is an "it", a person is a "them" — one shared component,
              but a component that calls someone "it" is worse than no copy. */}
          Nothing yet — add a picture of {label} so you recognise{' '}
          {kind === 'person' ? 'them' : 'it'} at a glance.
        </p>
      ) : (
        <ul className="mt-2 flex flex-wrap gap-2" data-testid="media-items">
          {items.map((item) => (
            <li key={item.media_id} className="group relative">
              <button
                type="button"
                onClick={() => setOpen(item)}
                className="block h-20 w-20 overflow-hidden rounded-lg border border-soft bg-surface-alt"
                title={item.caption || item.original_name}
              >
                {item.is_video ? (
                  <span className="flex h-full w-full items-center justify-center text-muted">
                    <Play className="h-6 w-6" />
                  </span>
                ) : (
                  <img
                    src={item.url}
                    alt={item.caption || `${label} photo`}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                )}
              </button>
              <button
                type="button"
                onClick={() => void remove(item)}
                aria-label="Remove"
                data-testid={`media-remove-${item.media_id}`}
                className="absolute -right-1.5 -top-1.5 hidden rounded-full border border-soft bg-surface p-1 text-muted shadow-sm hover:text-bad group-hover:block"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="mt-2 text-app-micro text-bad" role="alert">{error}</p>}

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6"
          onClick={() => setOpen(null)}
          role="dialog"
          aria-label={open.caption || 'Media'}
        >
          <div className="max-h-full max-w-3xl overflow-auto" onClick={(e) => e.stopPropagation()}>
            {open.is_video ? (
              <video src={open.url} controls className="lightbox-fit rounded-lg" />
            ) : (
              <img src={open.url} alt={open.caption || label} className="lightbox-fit rounded-lg" />
            )}
            <p className="mt-2 text-app-micro text-on-brand">
              {open.caption || open.original_name} · {sizeWord(open.bytes)}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
